import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "./Navbar";
import ListingInfoForm from "./ListingInfoForm";
import ListingTable from "./ListingTable";
import ListingDocumentsSection from "./ListingDocumentsSection";
import ListingDocumentSummarySection from "./ListingDocumentSummarySection";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";

const ListingInfo = () => {
  const [searchParams] = useSearchParams();
  const { isAgent, isAdmin, isSuperUser, isBroker } = useRole();
  const tabs = isBroker
    ? [{ id: "documents", label: "Upload Documents" }]
    : [
        { id: "list", label: "List" },
        { id: "info", label: "Listing Info" },
        { id: "documents", label: "Upload Documents" },
      ];
  const [activeTab, setActiveTab] = useState(() => {
    if (isBroker) return "documents";
    const tab = searchParams.get("tab");
    if (tab === "details") return "info";
    if (tab === "documents" || tab === "summary") return tab;
    return "list";
  });
  const [listings, setListings] = useState([]);

  useEffect(() => {
    if (isBroker) {
      setActiveTab("documents");
      return;
    }
    const tab = searchParams.get("tab");
    if (tab === "details") {
      setActiveTab("info");
      return;
    }
    if (tab === "documents" || tab === "summary") {
      setActiveTab(tab);
    }
  }, [searchParams, isBroker]);

  useEffect(() => {
    if (isBroker && activeTab !== "documents") {
      setActiveTab("documents");
    }
  }, [isBroker, activeTab]);

  useEffect(() => {
    if (activeTab !== "documents") return;
    const fetchListings = async () => {
      try {
        const response = await axiosInstance.get("/listings");
        const sorted = [...(response.data || [])].sort(
          (a, b) => (b.listingNumber || 0) - (a.listingNumber || 0)
        );
        setListings(sorted);
      } catch (error) {
        console.error("Error fetching listings:", error);
        toast.error("Failed to fetch listings");
      }
    };
    fetchListings();
  }, [activeTab]);

  return (
    <div className="flex flex-col">
      <Navbar />

      <div className="bg-white py-4 border-b">
        <div className="max-w-screen-2xl mx-auto px-4">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`text-lg font-medium px-3 py-2 hover:text-blue-900 transition-colors ${
                  activeTab === tab.id
                    ? "text-blue-900 border-b-2 border-blue-900"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
            {(isAdmin || isSuperUser) && (
              <button
                className={`text-lg font-medium px-3 py-2 hover:text-blue-900 transition-colors ${
                  activeTab === "summary"
                    ? "text-blue-900 border-b-2 border-blue-900"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("summary")}
              >
                Compliance Review
              </button>
            )}
          </nav>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto py-6">
        {isAgent && activeTab !== "documents" && activeTab !== "summary" && (
          <p className="px-4 mb-4 text-sm text-gray-600">
            You can add your listings here. Saves are stored as drafts until a
            Super User reviews them. Upload the listed documents in the Upload
            Documents section and save them, or enter the listing manually.
          </p>
        )}
        {isBroker && activeTab === "documents" && (
          <p className="px-4 mb-4 text-sm text-gray-600">
            Select a listing to view uploaded documents. You can view or download
            each file.
          </p>
        )}
        {!isBroker && activeTab === "list" && <ListingTable />}
        {!isBroker && activeTab === "info" && (
          <ListingInfoForm onSubmitSuccess={() => setActiveTab("list")} />
        )}
        {activeTab === "documents" && (
          <ListingDocumentsSection listings={listings} />
        )}
        {activeTab === "summary" && (isAdmin || isSuperUser) && (
          <div className="px-6">
            <ListingDocumentSummarySection />
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingInfo;
